import { RefObject, useCallback, useRef } from "react";
import { useUnmount } from "react-use";

export type DocumentPointerHandlers = {
  onPointerMove: (event: PointerEvent) => void;
  onPointerUp: (event: PointerEvent) => void;
  onPointerCancel: (event: PointerEvent) => void;
};

export const useDocumentPointerListeners = () => {
  const abortControllerRef = useRef<AbortController | null>(null);

  const stopListening = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  const startListening = useCallback(
    (handlersRef: RefObject<DocumentPointerHandlers>, pointerId: number) => {
      stopListening();

      const abortController = new AbortController();
      const options = { signal: abortController.signal };
      const listen = (
        type: "pointermove" | "pointerup" | "pointercancel",
        handlerName: keyof DocumentPointerHandlers
      ) =>
        document.addEventListener(
          type,
          (event) => {
            if (event.pointerId === pointerId) {
              handlersRef.current[handlerName](event);
            }
          },
          options
        );

      listen("pointermove", "onPointerMove");
      listen("pointerup", "onPointerUp");
      listen("pointercancel", "onPointerCancel");
      abortControllerRef.current = abortController;
    },
    [stopListening]
  );

  useUnmount(stopListening);

  return { startListening, stopListening };
};
